// Something that periodically checks a condition and
// executes a callback once that condition is true.
export class Poller {
    // The ID of the window setInterval call.
    private pollingId: number;

    // The amount of times that the condition has been checked.
    private attempts: number;

    // How long to wait between each poll.
    private readonly interval: number;

    // The maximum amount of times to check the condition.
    private readonly maxAttempts: number;

    // The condition to check.
    private readonly predicate: () => Promise<boolean>;

    // The function to execute when the condition is fulfilled.
    private readonly onSuccess: () => void;

    // The function to execute when the maximum polling attempts have been
    // hit and no success result was returned by the predicate.
    private readonly onFailure: () => void;

    private async poll() {
        try {
            if (await this.predicate()) {
                this.onSuccess();
                if (this.pollingId > -1) {
                    window.clearInterval(this.pollingId);
                }
                return;
            }
        } catch (e) {
            //console.log(`Poller attempt ${this.attempts + 1} failed. Max attempts: ${this.maxAttempts}`);
        }
        this.attempts += 1;
        if (this.attempts < this.maxAttempts) {
            this.pollingId = window.setTimeout(this.poll.bind(this), this.interval);
        } else {
            this.onFailure();
        }
    }

    /**
     * Constructs a new polling service.
     * @param predicate An async function that returns true when polling has returned a successful result.
     * @param onSuccess The callback to be called when polling has returned a successful result.
     * @param onFailure The callback to be called when polling has returned a failed result.
     * @param interval The number of milliseconds to wait between each poll.
     * @param maxAttempts The maximum amount of times to check the condition.
     */
    constructor(
        predicate: () => Promise<boolean>,
        onSuccess: () => void,
        onFailure: () => void,
        interval: number = 3000,
        maxAttempts: number = 10,
    ) {
        this.onSuccess = onSuccess;
        this.onFailure = onFailure;
        this.predicate = predicate;
        this.attempts = 0;
        this.pollingId = -1;
        this.interval = interval;
        this.maxAttempts = maxAttempts;
        this.poll();
    }
}
